import { useMemo } from 'react';
import { ORIGINAL_SIZE, PhotoSize, useKaiProApiState } from '../contexts';
import { PreWarmStatus, usePreWarmCache } from '../contexts/PreWarmCache';
import { buildScanPhotoPreWarmItems } from './scanPhotoPreWarm';

/**
 * Standalone hook that pre-warms the FileCache with a scan's photos.
 *
 * Depends only on the FileCache (via {@link usePreWarmCache}) and the API url
 * (via {@link useKaiProApiState}) — NOT the PhotoGallery. It maps each photoId to
 * the shared cache key + the scan-photo-data URL and hands the batch to the
 * pre-warm system (concurrency-managed downloads, skip+freshen already-cached,
 * progress).
 *
 * Pass `undefined` to stay inactive. Keyed by `scanId` + `size`, so different
 * sizes don't collide and concurrent users of the same scan/size share one
 * download set. Returns the live {@link PreWarmStatus} (progress, counts, phase).
 */
export const useCacheScanPhotos = (
  arg?: { scanId: string; photoIds: string[]; size?: PhotoSize },
): PreWarmStatus | undefined => {
  const { apiUrl } = useKaiProApiState();
  const size = arg?.size ?? ORIGINAL_SIZE;
  const preWarmArg = useMemo(
    () =>
      arg
        // apiUrl is in the key so a late-resolving base (native startup) re-keys to
        // the correct URLs; on web apiUrl is a stable '' so this never churns.
        ? { key: `scan-photos:${arg.scanId}:${size}:${apiUrl ?? ''}`, list: buildScanPhotoPreWarmItems(apiUrl, arg.photoIds, size) }
        : undefined,
    [apiUrl, arg?.scanId, size, arg?.photoIds],
  );
  return usePreWarmCache(preWarmArg);
};
